import React, { useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSchemes } from "../../../contexts/SchemesContext";
import { OffensiveGamePlan } from "../../../types/SchemesTypes";
import { CreateGameplanModal } from "./CreateGameplanModal";
import OffenseGameplanPlays from "./OffenseGameplanPlays";

export const OffenseGameplan = () => {
  const { offensiveGamePlans, deleteOffensiveGamePlan, offensiveSituations } = useSchemes();
  const [selectedGameplanId, setSelectedGameplanId] = useState<number | "">("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const handleGameplanChange = (event: SelectChangeEvent<number | "">) => {
    setSelectedGameplanId(event.target.value as number | "");
  };

  const handleCreateGameplan = () => {
    setCreateModalOpen(true);
  };

  const handleEditGameplan = () => {
    console.log("Edit game plan clicked for id:", selectedGameplanId);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedGameplanId) {
      const success = await deleteOffensiveGamePlan(selectedGameplanId);
      if (success) {
        setSelectedGameplanId("");
      }
    }
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleCreateModalClose = () => {
    setCreateModalOpen(false);
  };

  const selectedGameplan = offensiveGamePlans.find(
    (gp) => gp.id === selectedGameplanId
  );

  console.log('selected', selectedGameplan);

  return (
    <Box>
      {/* Spacer to move header down */}
      <Box sx={{ height: 16 }} />

      {/* Game Plan Selector Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          p: 2,
          bgcolor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel id="gameplan-select-label">Select Game Plan</InputLabel>
          <Select
            labelId="gameplan-select-label"
            id="gameplan-select"
            value={selectedGameplanId}
            label="Select Game Plan"
            onChange={handleGameplanChange}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {offensiveGamePlans.map((gameplan) => (
              <MenuItem key={gameplan.id} value={gameplan.id}>
                {gameplan.name} - {gameplan.game_details?.opponent_club?.name} -{" "}
                {new Date(gameplan.updated_at).toLocaleDateString()}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <IconButton
          onClick={handleCreateGameplan}
          color="primary"
          sx={{
            color: "primary.main",
            "&:hover": {
              bgcolor: "action.hover",
            },
          }}
        >
          <AddIcon />
        </IconButton>

        <IconButton
          onClick={handleEditGameplan}
          disabled={!selectedGameplanId}
          color="primary"
          sx={{
            bgcolor: selectedGameplanId ? "primary.main" : "action.disabled",
            color: "white",
            "&:hover": {
              bgcolor: selectedGameplanId ? "primary.dark" : "action.disabled",
            },
          }}
        >
          <EditIcon />
        </IconButton>

        <IconButton
          onClick={handleDeleteClick}
          disabled={!selectedGameplanId}
          color="error"
          sx={{
            bgcolor: selectedGameplanId ? "error.main" : "action.disabled",
            color: "white",
            "&:hover": {
              bgcolor: selectedGameplanId ? "error.dark" : "action.disabled",
            },
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Game Plan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the game plan "
            {selectedGameplan?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Gameplan Modal */}
      <CreateGameplanModal
        open={createModalOpen}
        onClose={handleCreateModalClose}
      />

      {/* Main content area - placeholder for now */}
      <Box>
        {selectedGameplanId && selectedGameplan ? (
          <OffenseGameplanPlays
            plays={selectedGameplan.plays}
            offensiveSituations={offensiveSituations}
          />
        ) : (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ textAlign: "center", mt: 4 }}
          >
            Please select a game plan or create a new one
          </Typography>
        )}
      </Box>
    </Box>
  );
};
